/*
 * this file is for the component of previously abandon checkout when user did not continue the payment
 */
import { useCallback, useState } from 'react';
import { View } from 'react-native';
import StyleSheet from 'react-native-media-query';
import { useSelector } from 'react-redux';
import { NavigationProp, useNavigation } from '@react-navigation/native';
import { Button, useTheme } from '@ui-kitten/components';
import dayjs from 'dayjs';
import lodashIsEmpty from 'lodash/isEmpty';

import { textCategory } from '../../../Components/Reservation/constants';
import ResponsiveText from '../../../Components/ResponsiveText';
import useAlert from '../../../Hooks/useAlert';
import routeList, { ReservationNavigatorProps } from '../../../Routes/list';
import { selectReservationCheckoutData } from '../../../RTK/utility/selectors';
import themeStyle from '../../../Theme/styles';
import breakpoints from '../../../Theme/styles/breakpoints';

const FinishMyReservation = ({ storeId }: { storeId: string }) => {
  const alert = useAlert();
  const theme = useTheme();
  const navigation = useNavigation<NavigationProp<ReservationNavigatorProps>>();
  const rcd = useSelector(selectReservationCheckoutData); // rcd = Reservation Checkout Data
  const [_, forceRerender] = useState(0);

  const _isCanStillFinishCheckout = useCallback(() => {
    if (lodashIsEmpty(rcd)) return false;
    else if (rcd.storeId !== storeId) return false;
    const selectedScheduleDayjs = dayjs(
      `${rcd.selectedDate} ${rcd.selectedTime}`,
    );
    return dayjs().isBefore(selectedScheduleDayjs);
  }, [rcd, storeId]);

  const _onContinue = () => {
    // fallback condition in case user stay on the page for too long and the previous checkout become outdated
    const isCanStillFinishCheckout = _isCanStillFinishCheckout();
    if (isCanStillFinishCheckout) {
      navigation.navigate(routeList.RES_CHECKOUT);
    } else {
      forceRerender((prev) => prev + 1);
      alert(
        'Reservation Expired',
        'Your pending reservation has expired and can no longer be completed.',
        { web: { buttonStatus: 'info' } },
      );
    }
  };

  if (!_isCanStillFinishCheckout()) return null;

  return (
    <View dataSet={{ media: ids.wrapper }} style={styles.wrapper}>
      <View
        style={[
          styles.container,
          { backgroundColor: `${theme['color-info-500']}0D` },
        ]}
      >
        <ResponsiveText
          category={textCategory.SMALL_BODY}
          style={themeStyle.spacingBottomMedium}
        >
          We've saved your progress so you can pickup right where you left off
        </ResponsiveText>
        <View style={styles.btnContainer}>
          <Button
            appearance="outline"
            status="info"
            style={styles.btn}
            onPress={_onContinue}
          >
            Finish My Reservation
          </Button>
        </View>
      </View>
    </View>
  );
};

const { ids, styles } = StyleSheet.create({
  wrapper: {
    ...themeStyle.pageBackground,
    [`@media ${breakpoints.lg}`]: {
      ...themeStyle.spacingTop,
    },
  },
  container: {
    ...themeStyle.pageHorizontalSpacing,
    ...themeStyle.pageVerticalSpacingMedium,
  },
  btnContainer: {
    ...themeStyle.pageBackground,
    borderRadius: 10,
  },
  btn: {
    borderRadius: 10,
  },
});

export default FinishMyReservation;
